/**
 * プランアップグレード誘導アラートの理由別文言
 * 出典: yoyacoo_alert_copy_proposals.xlsx（アラート文言案 No.1〜17）
 * No.11=15、No.12=16=17 は同一文言のため統合している
 */
export type UpgradeAlertReason =
  | "ADMIN_USER_LIMIT"
  | "GOOGLE_CALENDAR_SYNC"
  | "ZOOM_SYNC"
  | "GOOGLE_CALENDAR_HOME"
  | "EARNINGS_CSV_EXPORT"
  | "MAIL_CREATE"
  | "STEPMAIL_CREATE"
  | "CUSTOMER_CSV_IMPORT"
  | "CUSTOMER_CSV_TEMPLATE"
  | "CUSTOMER_CSV_EXPORT"
  | "ORDER_CSV_EXPORT"
  | "PAGE_PUBLISH_LIMIT"
  | "SUBSCRIPTION_MENU_LIMIT"
  | "IFRAME_EMBED"
  | "TAG_SETTING";

export const UPGRADE_ALERT_COPY: Record<
  UpgradeAlertReason,
  { title: string; description: string; linkText?: string; linkUrl?: string }
> = {
  ADMIN_USER_LIMIT: {
    title: "管理ユーザー数の上限に達しています",
    description:
      "現在のプランで追加できる管理ユーザー数に達したため、これ以上追加できません。人数を増やす場合は、プランをアップグレードしてください。",
  },
  GOOGLE_CALENDAR_SYNC: {
    title: "Googleカレンダー連携はライトプラン以上で利用できます",
    description:
      "予約枠の空き状況確認や予定の自動登録に利用するには、プランをアップグレードしてください。",
  },
  ZOOM_SYNC: {
    title: "Zoom連携はライトプラン以上で利用できます",
    description:
      "予約確定時にZoomミーティングURLを自動発行したい場合は、プランをアップグレードしてください。",
  },
  GOOGLE_CALENDAR_HOME: {
    title: "Googleカレンダー連携はライトプラン以上で利用できます",
    description:
      "直近の予定とGoogleカレンダーを同期するには、プランをアップグレードしてください。",
  },
  EARNINGS_CSV_EXPORT: {
    title: "売上一覧のCSV出力はライトプラン以上で利用できます",
    description:
      "売上データを外部で集計・保存したい場合は、プランをアップグレードしてください。",
  },
  MAIL_CREATE: {
    title: "メール配信はライトプラン以上で利用できます",
    description:
      "メルマガや個別メールで申込者・顧客に案内を送るには、プランをアップグレードしてください。",
  },
  STEPMAIL_CREATE: {
    title: "ステップメールはライトプラン以上で利用できます",
    description:
      "申込後の自動フォローや事前案内を自動化するには、プランをアップグレードしてください。",
  },
  CUSTOMER_CSV_IMPORT: {
    title: "顧客情報のCSV取り込みはライトプラン以上で利用できます",
    description:
      "既存の顧客リストをまとめて登録したい場合は、プランをアップグレードしてください。",
  },
  CUSTOMER_CSV_TEMPLATE: {
    title: "CSVテンプレートはライトプラン以上で利用できます",
    description:
      "顧客情報をCSVで取り込むためのテンプレートです。CSV取り込みを利用する場合は、プランをアップグレードしてください。",
  },
  CUSTOMER_CSV_EXPORT: {
    title: "顧客情報のCSV出力はライトプラン以上で利用できます",
    description:
      "顧客リストを外部で管理・分析したい場合は、プランをアップグレードしてください。",
  },
  ORDER_CSV_EXPORT: {
    title: "申込一覧のCSV出力はライトプラン以上で利用できます",
    description:
      "申込データを外部で集計・保存したい場合は、プランをアップグレードしてください。",
  },
  PAGE_PUBLISH_LIMIT: {
    title: "公開できるページ数の上限に達しています",
    description:
      "不要なページを非公開にするか、公開ページ数を増やせるプランへアップグレードしてください。",
  },
  SUBSCRIPTION_MENU_LIMIT: {
    title: "加入プランのサブスクメニュー数の上限に達しました。",
    description:
      "メニューを追加する場合は、プランのアップグレードをお願いいたします。※ページを離れる前に一時保存をしてください",
  },
  IFRAME_EMBED: {
    title: "iframe埋め込みはスタンダードプラン以上で利用できます",
    description:
      "ページを外部サイトに埋め込んで表示したい場合は、スタンダード以上へアップグレードしてください。",
  },
  TAG_SETTING: {
    title: "タグ設定はスタンダードプラン以上で利用できます",
    description:
      "Googleアナリティクスなどの計測タグを設置したい場合は、スタンダード以上へアップグレードしてください。",
  },
};

/**
 * ビジネスプラン専用の理由別文言（Option B, 2026-07-19 渡辺さん回答）
 * ビジネスプランは最上位プランのためアップグレード誘導ができず、
 * 上限系（管理ユーザー数・公開ページ数・サブスクメニュー数）のみ発生しうる。
 * 該当しない reason の場合は ErrorLimitByPlanModal 側で従来の共通文言にフォールバックする。
 */
export type UpgradeAlertBusinessReason =
  | "ADMIN_USER_LIMIT"
  | "PAGE_PUBLISH_LIMIT"
  | "SUBSCRIPTION_MENU_LIMIT";

export const UPGRADE_ALERT_BUSINESS_COPY: Record<
  UpgradeAlertBusinessReason,
  { title: string; description: string; linkText?: string; linkUrl?: string }
> = {
  ADMIN_USER_LIMIT: {
    title: "追加できる管理ユーザー数の上限に達しています。",
    description:
      "ビジネスプランで追加できる管理ユーザー数の上限に達しています。ご利用のない管理ユーザーを削除すると、新しいユーザーを追加できます。",
  },
  PAGE_PUBLISH_LIMIT: {
    // 冒頭のアラートは他プランの行と同様（渡辺さん回答より）
    title: UPGRADE_ALERT_COPY.PAGE_PUBLISH_LIMIT.title,
    description:
      "ビジネスプランで公開できるページ数の上限に達しているため、このページを公開できません。不要なページを非公開または削除すると、公開できるようになります。非公開・削除の方法は",
    linkText: "こちら",
    linkUrl: "https://note.com/eewww/n/n79411004602f",
  },
  SUBSCRIPTION_MENU_LIMIT: {
    title: "加入プランのサブスクメニュー数の上限に達しました。",
    description:
      "メニューを追加する場合は、プランのアップグレードをお願いいたします。※ページを離れる前に一時保存をしてください",
    linkUrl: "/user/accounts/plan?tab=2",
  },
};
